/**
 * CustomerAccountPopup — the /customers "click a customer" account dialog.
 *
 * 3 tabs (owner ask 2026-07-28, mirrors the terminal Customer Account):
 *   1. Open Invoices    — unpaid/partial, oldest first (account-summary)
 *   2. Account History  — invoices + payments interleaved, period-filterable
 *   3. Payment History  — every payment with its PP number (payment-history)
 *
 * Row clicks:
 *   - invoice rows  → parent's InvoiceDetailModal via onOpenInvoice(invoice_number)
 *     (the /sales-new print pipeline lives there — crisp thermal + dot matrix)
 *   - payment rows  → inline PP detail with Reprint Thermal / Reprint Dot Matrix
 *     via PrintEngine type 'proof_of_payment' — the exact same crisp route the
 *     post-payment print on /payments uses (owner: "that's the best route").
 *
 * Each tab keeps the 58mm statement print (PrintBridge.printStatement), same
 * as the terminal account view.
 */
import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ScrollArea } from './ui/scroll-area';
import {
  Printer, RefreshCw, Wallet, AlertTriangle, Loader2, Minus, Plus,
} from 'lucide-react';
import { api } from '../contexts/AuthContext';
import PrintEngine from '../lib/PrintEngine';
import PrintBridge from '../lib/PrintBridge';
import SendToPrintModal from './SendToPrintModal';
import ProofOfPaymentDialog from './ProofOfPaymentDialog';
import { toast } from 'sonner';

const PERIOD_OPTIONS = [
  { value: '7d',  label: 'Last 7 days'  },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: '3 months'     },
  { value: 'all', label: 'All time'     },
];

const TYPE_LABELS = {
  interest_charge: { label: 'Interest', cls: 'bg-amber-100 text-amber-700' },
  penalty_charge:  { label: 'Penalty',  cls: 'bg-red-100 text-red-700' },
  farm_expense:    { label: 'Farm',     cls: 'bg-green-100 text-green-700' },
  cash_advance:    { label: 'Cash Out', cls: 'bg-purple-100 text-purple-700' },
  historical_credit: { label: 'Notebook AR', cls: 'bg-teal-100 text-teal-700' },
};
const typeBadge = (saleType) => TYPE_LABELS[saleType] || { label: 'Invoice', cls: 'bg-slate-100 text-slate-700' };

const formatPHP = (v) => `₱${(parseFloat(v) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' });
  } catch { return iso?.slice(0, 10) || '—'; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-PH', { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return iso?.slice(0, 10) || '—'; }
};
// When a payment was RECORDED (owner 2026-08-12: the time locates it on CCTV).
// Same-day recordings show just the time under the date; a backdated payment
// shows the full recorded date+time so the difference is visible.
const fmtRecorded = (dateStr, recordedAt) => {
  if (!recordedAt) return '';
  const rec = new Date(recordedAt);
  if (isNaN(rec.getTime())) return '';
  const recLocalDay = rec.toLocaleDateString('en-CA');
  const sameDay = dateStr && recLocalDay === String(dateStr).slice(0, 10);
  return sameDay
    ? rec.toLocaleTimeString('en-PH', { hour: 'numeric', minute: '2-digit', hour12: true })
    : fmtDateTime(recordedAt);
};

export default function CustomerAccountPopup({ open, onOpenChange, customer, initialTab = 'open', onOpenInvoice }) {
  const [tab, setTab] = useState(initialTab);
  const [period, setPeriod] = useState('30d');
  const [summary, setSummary] = useState(null);       // account-summary payload
  const [payments, setPayments] = useState(null);     // payment-history payload
  const [loading, setLoading] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({});

  // PP detail sub-dialog
  // The PP window itself is the shared ProofOfPaymentDialog (2026-09-22) —
  // this popup only decides WHICH PP to show.
  const [ppNumber, setPpNumber] = useState(null);
  const [ppFallbackRow, setPpFallbackRow] = useState(null); // legacy payment w/o PP

  // 58mm statement print (same flow as the terminal account view)
  const [printTarget, setPrintTarget] = useState(null); // { printType, copies }
  const [printing, setPrinting] = useState(false);

  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  useEffect(() => {
    api.get('/settings/business-info').then(r => setBusinessInfo(r.data || {})).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!customer?.id || !open) return;
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        api.get(`/customers/${customer.id}/account-summary`, { params: { period } }),
        api.get(`/customers/${customer.id}/payment-history`),
      ]);
      setSummary(sRes.data);
      setPayments(pRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  }, [customer?.id, open, period]);

  useEffect(() => { load(); }, [load]);

  const openPP = (row) => {
    if (!row.pp_number) { setPpFallbackRow(row); return; }
    setPpNumber(row.pp_number);
  };

  const handleStatementPrint = async () => {
    if (!summary || !printTarget) return;
    setPrinting(true);
    try {
      for (let i = 0; i < (printTarget.copies || 1); i++) {
        await PrintBridge.printStatement(summary, businessInfo, printTarget.printType);
        if (i < (printTarget.copies || 1) - 1) await new Promise(r => setTimeout(r, 600));
      }
      toast.success(`Printed ${printTarget.copies} copy(ies)`);
    } catch (e) {
      toast.error(e.message || 'Print failed');
    } finally {
      setPrinting(false);
      setPrintTarget(null);
    }
  };

  const openInvoices = summary?.open_invoices ?? [];
  const historyRows = summary?.history ?? [];
  const openBalance = parseFloat(summary?.open_balance ?? customer?.balance ?? 0);
  const oldestOverdue = openInvoices.reduce((m, i) => Math.max(m, i.days_overdue || 0), 0);
  const totalReceived = (payments || []).filter(p => !p.voided && p.method !== 'Discount')
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  const PRINT_LABELS = { open_invoices: 'Open Invoices', history: 'History', payments: 'Payments' };

  const PeriodPills = () => (
    <div className="flex items-center gap-2 py-1">
      <span className="text-[10px] text-slate-400 uppercase tracking-wider">Period:</span>
      <div className="flex gap-1">
        {PERIOD_OPTIONS.map(p => (
          <button key={p.value} onClick={() => setPeriod(p.value)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              period === p.value ? 'bg-[#1A4D2E] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-emerald-300'
            }`}
            data-testid={`acct-period-${p.value}`}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );

  const PaymentCell = ({ row }) => (
    <button
      onClick={() => openPP(row)}
      className="font-mono text-emerald-700 hover:underline text-xs"
      title={row.pp_number ? 'View Proof of Payment' : 'Payment details'}
      data-testid={`acct-pp-link-${row.pp_number || 'none'}`}>
      {row.pp_number || row.receipt_number || '—'}
    </button>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-5xl max-h-[88vh] flex flex-col" data-testid="customer-account-popup">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }} className="flex items-center justify-between gap-3 pr-6">
              <span className="flex items-center gap-2">
                <Wallet size={18} className="text-[#1A4D2E]" />
                {customer?.name} — Customer Account
              </span>
              <Button variant="ghost" size="sm" onClick={load} disabled={loading} title="Refresh" data-testid="acct-refresh">
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {customer?.phone || 'No phone'}{customer?.address ? ` · ${customer.address}` : ''}
            </DialogDescription>
          </DialogHeader>

          {/* Summary strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs shrink-0">
            <div className={`rounded-md px-3 py-2 border ${openBalance > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Open Balance</div>
              <div className={`font-bold text-sm ${openBalance > 0 ? 'text-red-700' : 'text-emerald-700'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatPHP(openBalance)}
              </div>
            </div>
            <div className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Open Invoices</div>
              <div className="font-bold text-sm text-slate-800">{openInvoices.length}</div>
            </div>
            <div className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Oldest Overdue</div>
              <div className={`font-bold text-sm ${oldestOverdue > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
                {oldestOverdue > 0 ? `${oldestOverdue} day${oldestOverdue === 1 ? '' : 's'}` : '—'}
              </div>
            </div>
            <div className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Credit Limit</div>
              <div className="font-bold text-sm text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatPHP(customer?.credit_limit || 0)}
              </div>
            </div>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden mt-1">
            <TabsList className="shrink-0">
              <TabsTrigger value="open" data-testid="acct-tab-open">Open Invoices ({openInvoices.length})</TabsTrigger>
              <TabsTrigger value="history" data-testid="acct-tab-history">Account History</TabsTrigger>
              <TabsTrigger value="payments" data-testid="acct-tab-payments">Payment History</TabsTrigger>
            </TabsList>

            {/* ── Open Invoices ── */}
            <TabsContent value="open" className="flex-1 overflow-hidden mt-2">
              <ScrollArea className="h-[340px]">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                      <TableHead className="text-xs">Invoice #</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Due</TableHead>
                      <TableHead className="text-xs">Items</TableHead>
                      <TableHead className="text-xs text-right">Total</TableHead>
                      <TableHead className="text-xs text-right">Paid</TableHead>
                      <TableHead className="text-xs text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openInvoices.map(inv => {
                      const tb = typeBadge(inv.sale_type);
                      return (
                        <TableRow key={inv.invoice_id} className="cursor-pointer hover:bg-emerald-50/40"
                          onClick={() => onOpenInvoice?.(inv.invoice_number)}
                          data-testid={`acct-open-row-${inv.invoice_number}`}>
                          <TableCell className="text-sm font-medium text-blue-700">{inv.invoice_number}</TableCell>
                          <TableCell><Badge variant="outline" className={`text-[9px] ${tb.cls}`}>{tb.label}</Badge></TableCell>
                          <TableCell className="text-xs text-slate-500">{fmtDate(inv.date)}</TableCell>
                          <TableCell className="text-xs">
                            {fmtDate(inv.due_date)}
                            {inv.days_overdue > 0 && (
                              <span className="block text-[9px] font-semibold text-amber-700">{inv.days_overdue}d overdue</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-400 max-w-[180px] truncate" title={inv.items_preview}>
                            {inv.items_preview || '—'}
                          </TableCell>
                          <TableCell className="text-sm text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(inv.grand_total)}</TableCell>
                          <TableCell className="text-sm text-right text-emerald-600" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(inv.amount_paid)}</TableCell>
                          <TableCell className="text-sm text-right font-semibold text-red-600" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(inv.balance)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!loading && openInvoices.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-emerald-600">No open invoices — account is clear.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            {/* ── Account History ── */}
            <TabsContent value="history" className="flex-1 overflow-hidden mt-2 flex flex-col">
              <PeriodPills />
              <ScrollArea className="h-[310px]">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                      <TableHead className="text-xs">Ref #</TableHead>
                      <TableHead className="text-xs">Kind</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                      <TableHead className="text-xs text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyRows.map((row, i) => {
                      const isPay = row.type === 'payment';
                      const tb = typeBadge(row.sale_type);
                      return (
                        <TableRow key={i} className="cursor-pointer hover:bg-emerald-50/40"
                          onClick={() => isPay ? openPP(row) : onOpenInvoice?.(row.invoice_number)}
                          data-testid={`acct-history-row-${i}`}>
                          <TableCell className="text-sm font-medium">
                            {isPay
                              ? <span className="font-mono text-emerald-700 text-xs">{row.pp_number || row.receipt_number || row.invoice_number || '—'}</span>
                              : <span className="text-blue-700">{row.invoice_number}</span>}
                          </TableCell>
                          <TableCell>
                            {isPay
                              ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-0">Payment · {row.method || 'Cash'}</Badge>
                              : <Badge variant="outline" className={`text-[9px] ${tb.cls}`}>{tb.label}</Badge>}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{fmtDate(row.date)}</TableCell>
                          <TableCell className={`text-sm text-right ${isPay ? 'text-emerald-700 font-medium' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {isPay ? '+' : ''}{formatPHP(row.amount || row.grand_total || 0)}
                          </TableCell>
                          <TableCell className="text-xs text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {isPay ? '' : (parseFloat(row.balance) > 0
                              ? <span className="text-red-600 font-semibold">{formatPHP(row.balance)}</span>
                              : <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-0">Paid</Badge>)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!loading && historyRows.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No transactions in this period</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>

            {/* ── Payment History ── */}
            <TabsContent value="payments" className="flex-1 overflow-hidden mt-2">
              <ScrollArea className="h-[340px]">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: '#F1F0ED' }}>
                      <TableHead className="text-xs">Proof of Payment</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">For Invoice</TableHead>
                      <TableHead className="text-xs">Method</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                      <TableHead className="text-xs">Recorded By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(payments || []).map((p, i) => {
                      const isDiscount = p.method === 'Discount';
                      return (
                        <TableRow key={i}
                          className={`${p.voided ? 'opacity-40 line-through' : 'cursor-pointer hover:bg-emerald-50/40'} ${isDiscount ? 'bg-blue-50/40' : ''}`}
                          onClick={() => { if (!p.voided) openPP(p); }}
                          data-testid={`acct-payment-row-${i}`}>
                          <TableCell><PaymentCell row={p} /></TableCell>
                          <TableCell className="text-xs text-slate-500">
                            <div>{fmtDate(p.payment_date || p.date)}</div>
                            {p.payment_date && p.date && p.payment_date !== p.date && (
                              <div className="text-[10px] text-slate-400" title="The business day whose Z-Report carries this payment">Posted {fmtDate(p.date)}</div>
                            )}
                            {fmtRecorded(p.payment_date || p.date, p.recorded_at) && (
                              <div className="text-[10px] text-slate-400">{fmtRecorded(p.payment_date || p.date, p.recorded_at)}</div>
                            )}
                            {Array.isArray(p.date_edit_history) && p.date_edit_history.length > 0 && (
                              <div className="text-[10px] text-amber-600" title="The payment date was corrected — open the PP for the full trail">date corrected</div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{p.invoice_number}</TableCell>
                          <TableCell className="text-xs">
                            {isDiscount ? <Badge className="text-[9px] bg-blue-100 text-blue-700 border-0">Discount</Badge> : (p.method || 'Cash')}
                          </TableCell>
                          <TableCell className={`text-sm text-right font-medium ${isDiscount ? 'text-blue-600' : 'text-emerald-700'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatPHP(p.amount)}
                          </TableCell>
                          <TableCell className="text-xs text-slate-400">{p.recorded_by || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!loading && (payments || []).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No payment history</TableCell></TableRow>
                    )}
                    {(payments || []).filter(p => !p.voided && p.method !== 'Discount').length > 0 && (
                      <TableRow className="bg-slate-50 font-semibold">
                        <TableCell colSpan={4} className="text-right text-xs text-slate-500">Total Received</TableCell>
                        <TableCell className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(totalReceived)}</TableCell>
                        <TableCell />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {/* 58mm statement print footer — same flow as the terminal account view */}
          <div className="border-t pt-2 shrink-0 flex items-center justify-between gap-2">
            {printTarget ? (
              <div className="flex items-center gap-2 justify-between w-full">
                <span className="text-xs text-slate-600 font-medium truncate">{PRINT_LABELS[printTarget.printType]} — copies?</span>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setPrintTarget(p => ({ ...p, copies: Math.max(1, p.copies - 1) }))}
                    disabled={printTarget.copies <= 1} data-testid="acct-print-copies-minus"><Minus size={12} /></Button>
                  <span className="w-6 text-center text-sm font-bold tabular-nums">{printTarget.copies}</span>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                    onClick={() => setPrintTarget(p => ({ ...p, copies: Math.min(5, p.copies + 1) }))}
                    disabled={printTarget.copies >= 5} data-testid="acct-print-copies-plus"><Plus size={12} /></Button>
                  <Button size="sm" className="h-8 bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                    onClick={handleStatementPrint} disabled={printing} data-testid="acct-print-confirm">
                    {printing ? <Loader2 size={12} className="animate-spin mr-1" /> : <Printer size={12} className="mr-1" />}
                    {printing ? 'Printing…' : 'Print'}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-400"
                    onClick={() => setPrintTarget(null)} data-testid="acct-print-cancel">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Printer size={12} /> Print (58mm):</span>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" className="h-8 text-xs" disabled={!summary}
                    onClick={() => setPrintTarget({ printType: 'open_invoices', copies: 1 })}
                    data-testid="acct-print-open-invoices">Open Invoices</Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" disabled={!summary}
                    onClick={() => setPrintTarget({ printType: 'history', copies: 1 })}
                    data-testid="acct-print-history">History</Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" disabled={!summary}
                    onClick={() => setPrintTarget({ printType: 'payments', copies: 1 })}
                    data-testid="acct-print-payments">Payments</Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── PP detail — the shared window ── */}
      <ProofOfPaymentDialog open={!!ppNumber} onClose={() => setPpNumber(null)} ppNumber={ppNumber || ''}
        businessInfo={businessInfo} />

      {/* ── Legacy payment (no PP document) explainer ── */}
      <Dialog open={!!ppFallbackRow} onOpenChange={(o) => { if (!o) setPpFallbackRow(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="acct-pp-legacy">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle size={16} className="text-amber-500" /> No Proof of Payment document
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              This payment was recorded before Proof of Payment receipts existed, so there is no document to reprint.
            </DialogDescription>
          </DialogHeader>
          {ppFallbackRow && (
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Date</span><span>{fmtDate(ppFallbackRow.date)}</span></div>
              {ppFallbackRow.recorded_at && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Recorded</span><span>{fmtDateTime(ppFallbackRow.recorded_at)}</span></div>}
              <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Method</span><span>{ppFallbackRow.method || 'Cash'}</span></div>
              {ppFallbackRow.invoice_number && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">For invoice</span><span>{ppFallbackRow.invoice_number}</span></div>}
              {ppFallbackRow.reference && <div className="flex justify-between px-3 py-2"><span className="text-slate-500">Ref</span><span>{ppFallbackRow.reference}</span></div>}
              <div className="flex justify-between px-3 py-2 font-semibold"><span className="text-slate-600">Amount</span><span className="text-emerald-700">{formatPHP(ppFallbackRow.amount)}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
